// SurvMaps — shell dell'applicazione: header, scelta del comune e mappa del
// tipo giusto (Osimo gratuito, città per abbonati OmniSurv, demo PA).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

import Header from "./components/Header";
import CityPicker from "./components/CityPicker";
import LockedDialog from "./components/LockedDialog";
import { ToastProvider, useToast } from "./components/Toast";
import { Button, Dialog, LoadingOverlay } from "./components/ui";
import OsimoMap from "./maps/OsimoMap";
import GpkgMap from "./maps/GpkgMap";
import PaMap from "./maps/PaMap";
import { BACKEND, cityNameFromKey } from "./lib/config";
import { getJson } from "./lib/http";
import { useOmniAuth } from "./lib/omnisurv";
import { docsApi } from "./lib/docs";
import { getTenant } from "./tenant";

const CITY_PARAM = "citta";

// kind: sezione e accesso ("free" | "pro" | "pa"); gpkg: PRG dai gpkg di OmniSurv
// (GpkgMap) invece che dai geojson statici di Osimo (OsimoMap).
async function loadCities() {
  const [free, gpkg, pa] = await Promise.all([
    getJson(`${BACKEND}/api/cities.json`).catch(() => ({})),
    getJson(`${BACKEND}/api/gpkg/cities`)
      // backend precedente: solo /api/pro/cities, senza livello di accesso
      .catch(() => getJson(`${BACKEND}/api/pro/cities`).then((r) => ({ cities: r.cities.map((c) => ({ ...c, tier: "pro" })) })))
      .catch(() => ({ cities: [] })),
    getJson(`${BACKEND}/api/pa_cities.json`).catch(() => ({})),
  ]);
  const gpkgCities = (gpkg.cities || []).map((c) => ({ ...c, kind: c.tier === "free" ? "free" : "pro", gpkg: true }));
  const plainFree = Object.entries(free)
    .filter(([, v]) => v && v.center)
    .map(([key, v]) => ({ key, kind: "free", name: cityNameFromKey(key), center: v.center, zoom: v.zoom }));

  return {
    free: mergeFreeCities(plainFree, gpkgCities.filter((c) => c.kind === "free")),
    pro: gpkgCities.filter((c) => c.kind === "pro"),
    pa: Object.entries(pa)
      .filter(([, v]) => v && typeof v === "object" && "lat" in v)
      .map(([key, v]) => ({ key, kind: "pa", name: cityNameFromKey(key), lat: v.lat, lon: v.lon, zoom: v.zoom })),
  };
}

// Città gratuite presenti sia nell'elenco "classico" (cities.json, geojson
// statici di OsimoMap) sia tra i gpkg (oggi solo Osimo): un'unica card nel
// picker invece di due uguali — alla selezione si sceglie quale aprire
// (vedi il dialog "chooser" in Shell).
function mergeFreeCities(plain, gpkg) {
  const gpkgByKey = new Map(gpkg.map((c) => [c.key, c]));
  const used = new Set();
  const out = [];
  for (const legacy of plain) {
    const nuova = gpkgByKey.get(legacy.key);
    if (nuova) {
      out.push({ key: legacy.key, kind: "free", name: legacy.name, variants: { legacy, nuova } });
      used.add(legacy.key);
    } else {
      out.push(legacy);
    }
  }
  gpkg.forEach((c) => { if (!used.has(c.key)) out.push(c); });
  return out;
}

function setCityParam(key) {
  const url = new URL(window.location.href);
  if (key) url.searchParams.set(CITY_PARAM, key); else url.searchParams.delete(CITY_PARAM);
  window.history.replaceState(null, "", url);
}

function Shell() {
  const toast = useToast();
  const auth = useOmniAuth();
  const tenant = getTenant();
  const kinds = tenant === "pa" ? ["pa"] : ["free", "pro", "pa"];

  const [cities, setCities] = useState(null);
  const [city, setCity] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(true);
  const [locked, setLocked] = useState(null);
  const [chooser, setChooser] = useState(null); // città con due versioni (oggi Osimo): in attesa della scelta
  const [map, setMap] = useState(null);
  const [searchBounds, setSearchBounds] = useState(null);
  const [pending, setPending] = useState(() => new URLSearchParams(window.location.search).get(CITY_PARAM));

  useEffect(() => {
    loadCities().then(setCities).catch(() => {
      setCities({ free: [], pro: [], pa: [] });
      toast.error("Elenco dei comuni non disponibile.");
    });
  }, [toast]);

  // Documento intervento: comuni con NTA nel DB; ritorno dal Checkout Stripe.
  const [docsCities, setDocsCities] = useState([]);
  useEffect(() => {
    docsApi.cities().then((r) => setDocsCities(r.cities || [])).catch(() => {});
    const url = new URL(window.location.href);
    const esito = url.searchParams.get("abbonamento");
    if (esito) {
      if (esito === "ok") {
        toast.ok("Abbonamento SurvMaps attivato.");
        // il webhook Stripe può arrivare qualche secondo dopo il ritorno dal Checkout
        [2000, 6000, 15000].forEach((ms) => setTimeout(() => auth.refresh(), ms));
      } else {
        toast.info("Abbonamento non completato.");
      }
      url.searchParams.delete("abbonamento");
      window.history.replaceState(null, "", url);
    }
  }, [toast]); // eslint-disable-line react-hooks/exhaustive-deps
  const { status, token, email, plans, refresh } = auth;
  const docs = useMemo(
    () => ({
      available: !!city && docsCities.includes(city.key),
      auth: { status, token, email, plans, refresh },
    }),
    [city, docsCities, status, token, email, plans, refresh],
  );

  const open = useCallback((c) => {
    if (c.variants) {
      setChooser(c);
      return;
    }
    if (c.kind === "pro" && auth.status !== "paid") {
      setLocked(c);
      return;
    }
    setChooser(null);
    setLocked(null);
    setSearchBounds(null);
    setCity(c);
    setPickerOpen(false);
    setCityParam(c.key);
  }, [auth.status]);

  // ?citta=<chiave> apre direttamente il comune (anche da un link in OmniSurv).
  useEffect(() => {
    if (!pending || !cities || auth.status === "loading") return;
    const all = kinds.flatMap((k) => cities[k]);
    const c = all.find((x) => x.key === pending);
    setPending(null);
    if (c) open(c);
  }, [pending, cities, auth.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Abbonamento scaduto o login perso mentre si è su una città per abbonati.
  useEffect(() => {
    if (city?.kind === "pro" && (auth.status === "anon" || auth.status === "free")) {
      setLocked(city);
      setCity(null);
      setPickerOpen(true);
      setCityParam(null);
    }
  }, [auth.status, city]);

  // Stabile: cambia solo con refresh, così ProMap non ricarica le zone a ogni render.
  const cityRef = useRef(city);
  cityRef.current = city;
  const refreshAuth = auth.refresh;
  const onAuthError = useCallback(() => {
    refreshAuth();
    setLocked(cityRef.current);
    setCity(null);
    setPickerOpen(true);
    setCityParam(null);
  }, [refreshAuth]);

  const onMap = useCallback((m, bounds) => {
    setMap(m);
    setSearchBounds(bounds || null);
  }, []);

  const mapKey = city ? `${city.kind}:${city.key}` : null;

  return (
    <div className="sm-app">
      <Header
        city={city}
        onChangeCity={() => setPickerOpen(true)}
        map={map}
        searchBounds={searchBounds}
        auth={auth}
        showLinks={tenant !== "pa"}
      />

      {city?.kind === "free" && !city.gpkg && <OsimoMap key={mapKey} city={city} onMap={onMap} docs={docs} />}
      {city?.kind === "free" && city.gpkg && (
        <GpkgMap key={mapKey} city={city} onMap={onMap} onAuthError={onAuthError} docs={docs} />
      )}
      {/* "offline": OmniSurv momentaneamente irraggiungibile, la mappa resta aperta */}
      {city?.kind === "pro" && auth.token && (auth.status === "paid" || auth.status === "offline") && (
        <GpkgMap key={mapKey} city={city} token={auth.token} onMap={onMap} onAuthError={onAuthError} docs={docs} />
      )}
      {city?.kind === "pa" && <PaMap key={mapKey} city={city} onMap={onMap} />}

      {(pickerOpen || !city) && cities && (
        <CityPicker
          cities={cities}
          auth={auth}
          kinds={kinds}
          onSelect={open}
          onClose={city ? () => setPickerOpen(false) : null}
        />
      )}
      {!cities && <LoadingOverlay label="Caricamento comuni" />}

      {locked && <LockedDialog city={locked} auth={auth} onClose={() => setLocked(null)} />}

      {chooser && (
        <Dialog
          title={`${chooser.name}: quale versione?`}
          onClose={() => setChooser(null)}
          footer={(
            <>
              <Button onClick={() => open(chooser.variants.nuova)}>Nuova</Button>
              <Button variant="secondary" onClick={() => open(chooser.variants.legacy)}>Legacy</Button>
            </>
          )}
        >
          <p className="sm-panel-text">
            La versione <b>Nuova</b> usa la stessa mappa (zone, ricerca per foglio e particella, esportazione DXF)
            degli altri comuni abbonati. La versione <b>Legacy</b> resta con la vecchia estrazione PDF della
            normativa dall'area che disegni.
          </p>
        </Dialog>
      )}
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <Shell />
    </ToastProvider>
  );
}
